/* MUI */
import { Box, Button, Input, Paper, Typography } from '@mui/material';

/*  Shared  */
import { PartnerBackButton } from 'application/Shared/Components/Buttons/BackButton';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import {
    ATERA_API_DOCUMENTATION_URL,
    ATERA_SCRIPT_DOCUMENTATION_URL,
    FontSizes
} from 'application/Shared/Constants';
import { Notify } from 'application/Shared/Helpers';
import Core from 'application/Shared/Helpers/Functions';
import { Requests, Token } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/* Hooks */
import { getUserTheme } from 'hooks/dataAccess/User/userTheme';

/* React */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

/*  Components  */
import ImportantResources from './Components/ImportantResources';
import IntegrationSteps from './Components/Steps';

/* Constants */
import IntegrationScriptFilename from './Constants/IntegrationScriptsFilename.json';

/* Helpers */
import { getScript } from './Helpers/utils';

class AteraRMM extends Component {
    constructor(props) {
        super(props);
        this.state = {
            deploymentSteps: [],
            fields: [],
            fieldValues: [],
            hasToken: false
        };

        /* requests */
        this.Token = new Token();
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const state = {
            fields: [],
            fieldValues: [],
            hasToken: false
        };

        const partnerToken = await this.Token.getToken();

        if (partnerToken) {
            state.hasToken = true;
            try {
                const responseIntegrations = await axios.get('/integrations', {
                    cancelToken: this.requests.token(),
                    params: { key: 'atera' }
                });
                if (Core.isset(responseIntegrations, 'data', 'data', 0)) {
                    const fields = responseIntegrations.data.data;
                    state.fields = fields;
                    Object.keys(fields).forEach((fieldId) => {
                        const field = fields[fieldId];
                        if (field.value !== null) {
                            state.fieldValues[field.fieldKey] = field.value;
                        } else {
                            state.fieldValues[field.fieldKey] = field.default;
                        }
                    });
                    this.setDeploymentSteps(partnerToken);
                }
            } catch (err) {
                debug.e('Error loading Atera integration', err);
            }
        }

        this.setState(state);
    };

    unload = () => {
        this.requests.cancel();
        this.Token.cancel();
    };

    save = async () => {
        const { t } = this.props;
        const { fields, fieldValues } = this.state;

        Object.keys(fields).forEach(async (fieldId) => {
            const field = fields[fieldId];
            if (Core.isset(fieldValues, field.fieldKey)) {
                const payload = {
                    field: field.fieldKey,
                    key: 'atera',
                    value: fieldValues[field.fieldKey]
                };
                await axios.post('/integrations/', payload);
            }
        });

        Notify.success(t('PartnerIntegration.save_successful'));
    };

    setDeploymentSteps = async (partnerToken) => {
        const { t } = this.props;

        const scriptCopyValue = await getScript({
            filename: IntegrationScriptFilename.ateraRMMScript,
            token: partnerToken
        });

        const deploymentSteps = [
            {
                component: (
                    <Typography variant="h4">
                        {t('Integrations.script_code')}
                    </Typography>
                ),
                step: false
            },
            {
                note: t('Integrations.note'),
                step: false
            },
            {
                component: t('Integrations.AteraRMM.step1'),
                step: true
            },
            {
                component: t('Integrations.AteraRMM.step2'),
                step: true,
                stepUrl: ATERA_SCRIPT_DOCUMENTATION_URL
            },
            {
                component: t('Integrations.AteraRMM.step3'),
                copyValue: scriptCopyValue,
                step: true
            }
        ];
        this.setState({ deploymentSteps });
    };

    renderIntegrationBody() {
        const { session, t } = this.props;
        const { deploymentSteps, hasToken } = this.state;

        const userTheme = getUserTheme(session?.user?.theme);

        return (
            <Box width="100%">
                <Box>
                    <img
                        src={`/images/${userTheme}/integrations/atera.png`}
                        alt={t('Integrations.AteraRMM.logo')}
                        style={{ width: '200px' }}
                    />
                    <hr />
                </Box>
                <Box mt="16px">
                    <ImportantResources />
                </Box>
                {hasToken && deploymentSteps.length > 0 && (
                    <>
                        <Box mt="32px">
                            <Box>
                                <Typography variant="h4">
                                    {t('Common.configuration')}
                                </Typography>
                                <Box mt="8px">
                                    {t('Integrations.AteraRMM.docs')}
                                </Box>
                                <a
                                    href={ATERA_API_DOCUMENTATION_URL}
                                    target="_blink"
                                    className="link--secondary"
                                    data-trackid="partner_integrations_atera_docsURL"
                                >
                                    {ATERA_API_DOCUMENTATION_URL}
                                </a>
                            </Box>
                            <Box mt="16px">{this.renderInputFields()}</Box>

                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    data-trackid="partner_integrations_atera_save"
                                    color="primary"
                                    onClick={() => this.save()}
                                    variant="contained"
                                >
                                    {t('Common.save_settings')}
                                </Button>
                            </Box>

                            <hr />
                        </Box>

                        <Box mt="16px">
                            <IntegrationSteps steps={deploymentSteps} />
                        </Box>
                    </>
                )}
            </Box>
        );
    }

    renderInputFields() {
        const { t } = this.props;
        const { fields, fieldValues } = this.state;

        return fields.map((field) => (
            <Box mb="16px" key={field.fieldKey}>
                <Box as="span">{t(field.label)}</Box>
                <TooltipQ text={t(field.tooltip)} size={FontSizes[1]} />
                <Input
                    autoFocus
                    type="password"
                    onChange={(e) => {
                        this.setState({
                            fieldValues: {
                                ...fieldValues,
                                [field.fieldKey]: e.target.value
                            }
                        });
                    }}
                    fullWidth
                    autoComplete="off"
                    value={fieldValues[field.fieldKey]}
                />
            </Box>
        ));
    }

    render() {
        const { onboarding = false, t } = this.props;

        return onboarding ? (
            this.renderIntegrationBody()
        ) : (
            <>
                <Box width="100%">
                    <Typography variant="h3">
                        {t('Integrations.AteraRMM.header')}
                    </Typography>
                    <hr />
                </Box>
                <PartnerBackButton
                    linkTo="/integrations/partnerLevel"
                    textOverride="Integrations.Common.back"
                />
                <Paper
                    elevation={0}
                    variant="outlined"
                    style={{ padding: '16px' }}
                >
                    {this.renderIntegrationBody()}
                </Paper>
            </>
        );
    }
}
export default withTranslation()(AteraRMM);
